import { useCallback, useMemo } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import nth from 'lodash/nth';
import Dialog, { Clip, Next } from '../molecules/Dialog';
import Grid from '../atoms/Grid';
import Image from '../atoms/Image';
import Text from '../atoms/Text';
import TextBlock from '../molecules/TextBlock';
import AutofitText from '../molecules/AutofitText';
import Centered from '../atoms/Centered';
import Assets, { getImageByKey } from '../../assets/Assets';
import type { Image as ImageType } from '../../assets/Assets';
import type { Combination } from '../../data/Combinations';
import { findConceptByName } from '../../data/Concepts';
import type { Concept } from '../../data/Concepts';

const Caption = styled.div`
	background-color: ${(props) => props.theme.colors.darkRed};
	border-radius: 9999px;
	color: ${(props) => props.theme.colors.white};
	font: ${(props) => props.theme.font.default};
	font-size: 7vmin;
	padding-left: 0.5em;
	padding-right: 0.5em;
	text-shadow: ${(props) => props.theme.shadow.caption};
`;

const Picture = styled.div`
	background-color: ${(props) => props.theme.colors.veryLightBlue};
	border-radius: 10vmin;
	bottom: 0;
	left: 0;
	overflow: hidden;
	position: absolute;
	right: 0;
	top: 3.5vmin;
`;

const RedWithShadow = styled.div`
	color: ${(props) => props.theme.colors.darkRed};
	filter: ${(props) =>
		`drop-shadow(${props.theme.shadow.imageButtonBorder}) drop-shadow(${props.theme.shadow.imageButton})`};
`;

export interface Props {
	concept: Concept;
	combination: Combination;
	onNext: (concept: Concept) => void;
}

const CombinationDialog: FunctionComponent<Props> = (props: Props) => {
	const { concept, combination, onNext } = props;
	const { t } = useTranslation();

	const getNthConcept = useCallback(
		(combination: Combination, index: number): Concept | undefined => {
			const conceptKey = nth(combination.concepts, index);
			if (conceptKey) {
				return findConceptByName(conceptKey);
			}
			return undefined;
		},
		[]
	);

	const firstConcept = useMemo(
		() => getNthConcept(combination, 0),
		[combination, getNthConcept]
	);
	const secondConcept = useMemo(
		() => getNthConcept(combination, 1),
		[combination, getNthConcept]
	);

	const getConceptImage = useCallback(
		(concept?: Concept): ImageType | undefined => {
			const imageKey = concept?.images.after;
			if (imageKey) {
				return getImageByKey(imageKey);
			}
			return undefined;
		},
		[]
	);

	const imageFirstConcept = useMemo(
		() => getConceptImage(firstConcept),
		[firstConcept, getConceptImage]
	);
	const imageSecondConcept = useMemo(
		() => getConceptImage(secondConcept),
		[secondConcept, getConceptImage]
	);

	return (
		<Dialog>
			<Clip>
				<div
					style={{
						position: 'absolute',
						left: '2%',
						right: '2%',
						top: '4%',
						bottom: '20%'
					}}
				>
					<Grid
						columns={2}
						rows={1}
						position="absolute"
						width="100%"
						height="100%"
					>
						<Picture>
							{imageFirstConcept && (
								<Image src={imageFirstConcept} width="100%" height="100%" />
							)}
						</Picture>
						<Picture>
							{imageSecondConcept && (
								<Image src={imageSecondConcept} width="100%" height="100%" />
							)}
						</Picture>
					</Grid>
					<Centered position="absolute" left="0" right="0">
						<Caption>
							<Trans>concept_combination.great_combo</Trans>
						</Caption>
					</Centered>
					<Centered position="absolute" left="0" right="0" top="0" bottom="0">
						<RedWithShadow>
							<Image src={Assets.images.plus} width="20vmin" height="20vmin" />
						</RedWithShadow>
					</Centered>
				</div>
				<TextBlock
					height="20%"
					position="absolute"
					left="25%"
					right="25%"
					bottom="5%"
				>
					<AutofitText>
						<Text content={t(combination.texts.reason)} />
					</AutofitText>
				</TextBlock>
			</Clip>
			<Next onClick={() => onNext(concept)}>
				<Trans>concept_combination.next</Trans>
			</Next>
		</Dialog>
	);
};

export default CombinationDialog;
